import {Grid, Typography} from '@material-ui/core';
import {useQuoteField} from 'Quoting/components/hooks/useHandleQuoteFieldChange';
import {FormRowGrid, GridRowProps} from 'Quoting/components/shared/GridRow';
import {
  DualComponentConditionalSection,
  QuoteFormDualConditionalSection,
} from 'Quoting/components/shared/QuoteFormConditionalSection';
import {QuoteFormElement} from 'Quoting/components/shared/QuoteFormElement';
import {FormWrapper} from 'Quoting/components/shared/QuoteFormWrapper';
import React from 'react';
import {useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {getBenefitArrayPaths, getBenefitSchedulePropertyPaths} from '../Common/PropertyPaths';

const coverageIndex = 0;
const benefitName = 'cancerConditionsDetails.conditionsDetails';

const InitialDiagnosisBlock = () => {
  const {getQuoteFieldValue, updateQuoteFieldValue} = useQuoteField();
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);

  const initialPath = getBenefitArrayPaths(selectedPlan?.planIndex, coverageIndex, benefitName, 0);

  const initialRows: GridRowProps[] = [
    {
      rowProps: {key: 'initial-cancer-conditions-row-1'},
      items: [
        {
          key: 'initial-cancer-conditions-invasive-cancer',
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="Invasive Cancer (100%)"
              value={getQuoteFieldValue<number>(initialPath.invasiveCancerPct)}
              id={initialPath.invasiveCancerPct}
              onChange={e => updateQuoteFieldValue(initialPath.invasiveCancerPct, Number(e.target.value) / 100)}
              adornment="percentage"
              disabled
              required
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
      ],
    },
    {
      rowProps: {key: 'initial-cancer-conditions-row-2'},
      items: [
        {
          key: 'initial-cancer-conditions-carcinoma',
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="Carcinoma in Situ (25%)"
              value={getQuoteFieldValue<number>(initialPath.carcinomaSituPct)}
              id={initialPath.carcinomaSituPct}
              onChange={e => updateQuoteFieldValue(initialPath.carcinomaSituPct, Number(e.target.value) / 100)}
              adornment="percentage"
              disabled
              required
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
      ],
    },
  ];
  
  return (
    <Grid spacing={1} style={{padding: '0 2rem'}} container alignItems="center">
      <Typography component="span" variant="subtitle1" style={{fontSize: '0.875rem'}}>
        Initial Diagnosis (% of Plan Benefit)
      </Typography>
      <FormRowGrid rows={initialRows} />
    </Grid>
  );
};

export const RecurrenceDiagnosisBlock = () => {
  const {getQuoteFieldValue, updateQuoteFieldValue} = useQuoteField();
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);
  const recurrencePath = getBenefitArrayPaths(selectedPlan?.planIndex, coverageIndex, benefitName, 1);

  const recurranceRows: GridRowProps[] = [
    {
      rowProps: {key: 'recurrance-cancer-conditions-row-1'},
      items: [
        {
          key: 'recurrance-cancer-conditions-invasive-cancer',
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="Invasive Cancer"
              value={getQuoteFieldValue<number>(recurrencePath.invasiveCancerPct)}
              id={recurrencePath.invasiveCancerPct}
              onChange={e => updateQuoteFieldValue(recurrencePath.invasiveCancerPct, Number(e.target.value) / 100)}
              adornment="percentage"
              disabled
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
      ],
    },
    {
      rowProps: {key: 'recurrance-cancer-conditions-row-2'},
      items: [
        {
          key: 'recurrance-cancer-conditions-carcinoma',
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="Carcinoma in Situ"
              value={getQuoteFieldValue<number>(recurrencePath.carcinomaSituPct)}
              id={recurrencePath.carcinomaSituPct}
              onChange={e => updateQuoteFieldValue(recurrencePath.carcinomaSituPct, Number(e.target.value) / 100)}
              adornment="percentage"
              disabled
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
      ],
    },
  ];
  return (
    <Grid spacing={1} style={{padding: '0 2rem'}} container alignItems="center">
      <Typography component="span" variant="subtitle1" style={{fontSize: '0.875rem'}}>
        Recurrence (% of Plan Benefit)
      </Typography>
      <FormRowGrid rows={recurranceRows} />
    </Grid>
  );
};

export const CancerConditionsBlockGVCI4 = () => {
  const {getQuoteFieldValue, updateQuoteFieldValue} = useQuoteField();
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);

  const recurrenceDetailsPath = getBenefitSchedulePropertyPaths(0, selectedPlan?.planIndex, coverageIndex)
    .isRecurrenceBenefitApplied;

  const cancerConditionsRecurrenceBenefit: DualComponentConditionalSection[] = [
    {
      PrimaryComponent: InitialDiagnosisBlock,
      SecondaryComponent: RecurrenceDiagnosisBlock,
      key: 'RecurrenceDiagnosisBlock',
      label: 'RECURRENCE BENEFIT',
      disabled: true,
      isApplied: getQuoteFieldValue(recurrenceDetailsPath),
      onToggleVisibility: (visibility: boolean) => updateQuoteFieldValue(recurrenceDetailsPath, visibility),
    },
  ];

  return (
    <FormWrapper title="Cancer Conditions" collapsible withSeparator>
      <Grid container alignItems="center">
        <QuoteFormDualConditionalSection sections={cancerConditionsRecurrenceBenefit} />
      </Grid>
    </FormWrapper>
  );
};
